import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { SetStateAction } from "react";

export type EventStatusFilter = "all" | "active" | "inactive" | "finished";
export type EventDateSortOrder = "asc" | "desc";

export interface EventsFiltersState {
  eventId?: number;
  eventTypeId?: number;
  eventSubTypeId?: number;
  startDate?: string;
  endDate?: string;
  eventName: string;
  tenantId?: string | null;
  featured?: boolean;
  eventStatus: EventStatusFilter;
  dateSortOrder: EventDateSortOrder;
}

export const toEventDateParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseEventDateParam = (value?: string): Date | null => {
  if (!value) return null;

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (isoMatch) {
    const [, year, month, day] = isoMatch.map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Estado al cargar la pantalla (fecha desde = hoy). */
export const getDefaultEventsFilters = (): EventsFiltersState => ({
  eventId: undefined,
  eventTypeId: undefined,
  eventSubTypeId: undefined,
  startDate: toEventDateParam(new Date()),
  endDate: undefined,
  eventName: "",
  tenantId: undefined,
  featured: undefined,
  eventStatus: "all",
  dateSortOrder: "asc",
});

/** Estado tras "Limpiar filtros". */
export const getClearedEventsFilters = (): EventsFiltersState =>
  getDefaultEventsFilters();

const initialState: EventsFiltersState = getDefaultEventsFilters();

const eventsFiltersSlice = createSlice({
  name: "eventsFilters",
  initialState,
  reducers: {
    setEventId: (state, action: PayloadAction<number | undefined>) => {
      state.eventId = action.payload;
    },
    setEventTypeId: (state, action: PayloadAction<number | undefined>) => {
      state.eventTypeId = action.payload;
    },
    setEventSubTypeId: (state, action: PayloadAction<number | undefined>) => {
      state.eventSubTypeId = action.payload;
    },
    setStartDate: (state, action: PayloadAction<string | undefined>) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action: PayloadAction<string | undefined>) => {
      state.endDate = action.payload;
    },
    setEventName: (state, action: PayloadAction<string>) => {
      state.eventName = action.payload;
    },
    setTenantId: (state, action: PayloadAction<string | null | undefined>) => {
      state.tenantId = action.payload;
    },
    setFeatured: (state, action: PayloadAction<boolean | undefined>) => {
      state.featured = action.payload;
    },
    setEventStatus: (state, action: PayloadAction<EventStatusFilter>) => {
      state.eventStatus = action.payload;
    },
    setDateSortOrder: (state, action: PayloadAction<EventDateSortOrder>) => {
      state.dateSortOrder = action.payload;
    },
    resetEventsFilters: () => getClearedEventsFilters(),
  },
});

export const {
  setEventId,
  setEventTypeId,
  setEventSubTypeId,
  setStartDate,
  setEndDate,
  setEventName,
  setTenantId,
  setFeatured,
  setEventStatus,
  setDateSortOrder,
  resetEventsFilters,
} = eventsFiltersSlice.actions;

export const selectEventsFilters = (state: {
  eventsFilters: EventsFiltersState;
}) => state.eventsFilters;

export const resolveFilterValue = <T,>(
  value: SetStateAction<T>,
  current: T
): T =>
  typeof value === "function" ? (value as (prev: T) => T)(current) : value;

export default eventsFiltersSlice.reducer;
